import { useNavigate, useRouter } from "@tanstack/react-router";
import type { AnyRouter } from "@tanstack/react-router";

/**
 * "Voltar", inside a caderno — and the promise that it never leaves one.
 *
 * THE BUG THIS EXISTS FOR. Every fullscreen screen in a caderno (the reader,
 * Praticar, an exame, a simulado) walks the history for its way out, so "voltar"
 * returns wherever the student opened the tópico FROM — the trilha, a matéria,
 * the Painel. That is right nearly always, and wrong in exactly one case: when
 * the student got here from the hub tutor. Lume hands out links, the student taps
 * one from /chat, and the entry behind this screen is the hub — so "voltar" threw
 * them out of the caderno they had only just arrived in, past everything they
 * might have wanted to go back to.
 *
 * So the rule is no longer "go back" but "go back IF that stays in this caderno".
 * When it would not, the fallback takes over — the trilha, which is the caderno's
 * own way of saying "here is where you are and what is next".
 *
 * THE SAME RULE ANSWERS THE DEEP LINK, which the old code handled separately:
 * a refresh or a link opened cold has no in-app history at all, so it fails the
 * same test and lands in the same place. One condition instead of two.
 */

/**
 * Pathname by history index — because the router will say WHETHER it can go back
 * and never says WHERE that would land, and the browser cannot be asked.
 *
 * Keyed on TanStack's own `__TSR_index` rather than pushed onto a stack, which is
 * what makes it right under the two things a stack gets wrong: a `replace` writes
 * the same index and overwrites the entry it replaced (the manual→praticar hop
 * does exactly this — see ManualReader), and walking back and forth re-visits
 * indices instead of growing.
 */
const visited = new Map<number, string>();

/** Called once, from App, with the app's router. */
export function trackHistory(router: AnyRouter): void {
  const record = () => {
    const { pathname, state } = router.state.location;

    // Typed as always present, checked anyway: this runs at boot, and a missing
    // index here would be a blank app rather than a back button that guesses.
    if (typeof state.__TSR_index === "number") {
      visited.set(state.__TSR_index, pathname);
    }
  };

  // The entry the app booted on: it is already in the history and no navigation
  // will be resolved for it.
  record();
  router.subscribe("onResolved", record);
}

/** Where the back button would land, or null when that is not known. */
function previousPathname(router: AnyRouter): string | null {
  return visited.get(router.state.location.state.__TSR_index - 1) ?? null;
}

function isInsideCaderno(
  pathname: string | null,
  cadernoId: number | string
): boolean {
  if (pathname === null) {
    return false;
  }

  // The caderno's own root, and anything under it. `startsWith` on the root alone
  // would count /caderno/12 as inside /caderno/1.
  const root = `/caderno/${cadernoId}`;

  return pathname === root || pathname.startsWith(`${root}/`);
}

/**
 * The way out of a fullscreen screen inside a caderno.
 *
 * @param fallback where to go when back would leave the caderno. Defaults to the
 *   trilha; Praticar's build flows pass their own, because a half-built simulado
 *   belongs back at the screen that offered it and not at the top of the roteiro.
 */
export function useCadernoBack(
  cadernoId: number | string,
  fallback?: () => void
): () => void {
  const router = useRouter();
  const navigate = useNavigate();

  return () => {
    if (
      router.history.canGoBack() &&
      isInsideCaderno(previousPathname(router), cadernoId)
    ) {
      router.history.back();
      return;
    }

    if (fallback !== undefined) {
      fallback();
      return;
    }

    void navigate({
      params: { cadernoId: String(cadernoId) },
      to: "/caderno/$cadernoId/trilha",
    });
  };
}
